import _ from 'lodash';
import moment from 'moment-timezone';
import { TRAYA_APP_LINK } from 'config/env';

/**
 * Builds the CRM object the female pre-sales prototype
 * (female_smart_assist_V7_final.html) derives every section from.
 *
 * The prototype ships its own DERIVE block that computes root causes, the kit
 * and the expectation plan from the hair-test answers, because it has no
 * backend. The real CRM already computes all three server-side, so those are
 * preferred here and DERIVE is only a fallback — deriving client-side when the
 * backend has already decided is how the male cohort ended up showing a kit
 * that disagreed with the result page.
 *
 * Anything genuinely missing stays null. The sections render "—" for a null
 * rather than inventing a value.
 */

/* ── Hair-test answers ────────────────────────────────────────────────
 * Ids and question text both come from the female hair test itself
 * (modules/form_formulation/formData.js), so these are the real stored values
 * rather than inferred ones. Each field still matches on id OR text, because
 * older responses carry only the text — the same dual approach
 * PCSmartAssistWizardFemale.jsx:155-173 uses.
 */
const ANSWER_FIELDS = {
  hairfall_amount:    { ids: ['hairFallIntensity'],   re: /how much hairfall/i },
  hairfall_duration:  { ids: ['hairFallTimeframe'],   re: /how long have you been experiencing/i },
  // Legacy ids kept: PrescriptionForm.jsx:7 still reads volume as 2e / hair_vol1.
  hair_volume:        { ids: ['hairLossStage', '2e', 'hair_vol1'], re: /current hair volume/i },
  hair_feel:          { ids: ['hairQualityFeelLike'], re: /hair feel like/i },
  hair_concerns:      { ids: ['hairGoalConcern'],     re: /hair concerns you want to address/i },
  scalp_type:         { ids: ['scalpFeelLike'],       re: /scalp feel like/i },
  past_treatments:    { ids: ['hairTreatment'],       re: /hair treatments.*past 2 years/i },
  dandruff:           { ids: ['scalpDandruff'],       re: /experience with dandruff/i },
  genetics:           { ids: ['hairGenetics'],        re: /genetic issue in your family/i },
  health_conditions:  { ids: ['healthConditions'],    re: /health conditions currently/i },
  hormonal_symptoms:  { ids: ['pcosSelfDiagnosis'],   re: /which of these do you tend to experience/i },
  life_stage:         { ids: ['femaleLifestage'],     re: /life stages currently/i },
  recent_events:      { ids: ['majorHealthIssue'],    re: /in the last 1 year/i },
  digestion:          { ids: ['gutHealth', '3di'],    re: /stomach\/digestion|gas, acidity or bloating/i },
  sleep:              { ids: ['sleepPattern'],        re: /how well do you sleep/i },
  stress:             { ids: ['stressLevels'],        re: /stress level/i },
  energy:             { ids: ['energyLevels'],        re: /typical energy/i },
  supplements:        { ids: ['supplementsIntake'],   re: /supplements or vitamins/i },
  food:               { ids: ['dietaryHabits'],       re: /food habits/i },
};

/**
 * Responses are grouped per submission (synthetic_id) and the newest group is
 * the latest engagement — the same grouping Customer Info -> Consult shows,
 * where each tab is one submission date (SideSubTab.jsx:156-179).
 *
 * That tab renders `caseFormResponses` (GET case-form-responses/{caseId}), so
 * prefer it and fall back to customerData.responses, which is the same shape
 * but not always populated.
 */
/**
 * Every submission on the case, newest first, grouped the way Customer Info ->
 * Consult groups its date tabs (SideSubTab.jsx:156-179): one group per
 * `synthetic_id`, ordered by when the submission was started.
 *
 * This is the single definition of "a submission" for the whole female flow.
 * It previously existed twice with two different rules — the wizard took the
 * group whose FIRST row was newest, the answers took the group holding the
 * newest row overall. When a lead amends an older test those two pick
 * different submissions, so the kit came off one hair test and the answers off
 * another.
 */
export function submissionsNewestFirst(customerData, caseFormResponses) {
  const all = (caseFormResponses && caseFormResponses.length)
    ? caseFormResponses
    : (_.get(customerData, 'responses', []) || []);
  const withId = all.filter(r => _.get(r, 'synthetic_id'));
  if (!withId.length) return all.length ? [{ rows: all, syntheticId: null, status: null }] : [];

  const groups = _.groupBy(withId, r => _.get(r, 'synthetic_id'));
  return Object.keys(groups)
    .map((sid) => {
      const rows = _.sortBy(groups[sid], r => new Date(_.get(r, 'created_at', 0)).getTime());
      return {
        syntheticId: sid,
        rows,
        status: _.get(rows, [0, 'status'], null),
        startedAt: new Date(_.get(rows, [0, 'created_at'], 0)).getTime(),
      };
    })
    .sort((a, b) => b.startedAt - a.startedAt);
}

/**
 * The submission the Result is read from.
 *
 * Consult only loads a Result for a submission whose status is "filled"
 * (SideSubTab.jsx:144-147) — an unfilled one has no result yet, so asking for
 * it returns an empty kit. So: newest filled submission; if none of them carry
 * a status at all, the newest submission.
 */
export function latestFilledSubmission(customerData, caseFormResponses) {
  const all = submissionsNewestFirst(customerData, caseFormResponses);
  if (!all.length) return null;
  return all.find(g => g.status === 'filled')
    || (all.every(g => !g.status) ? all[0] : null);
}

function latestResponses(customerData, caseFormResponses) {
  const latest = latestFilledSubmission(customerData, caseFormResponses)
    || submissionsNewestFirst(customerData, caseFormResponses)[0];
  return _.get(latest, 'rows', []);
}

/**
 * Answers from the latest submission, falling back to older ones per question.
 *
 * A retake only carries the questions it asked, so reading one submission left
 * anything it skipped blank — "What is your experience with dandruff these
 * days?" showed an em dash on a lead who had answered it on the previous
 * hair test. Each field takes the newest submission that actually answered it,
 * so the latest answer always wins and nothing silently disappears.
 */
function readAnswers(customerData, caseFormResponses) {
  const latest = latestResponses(customerData, caseFormResponses);
  const older = submissionsNewestFirst(customerData, caseFormResponses)
    .map(sub => _.get(sub, 'rows', []));

  const answered = (rows, ids, re) => {
    const hit = rows.find(r => ids.includes(_.get(r, 'question_id')))
      || rows.find(r => re.test(String(_.get(r, 'question', ''))));
    const value = _.get(hit, 'response', null);
    return value === '' ? null : value;
  };

  const out = {};
  Object.entries(ANSWER_FIELDS).forEach(([key, { ids, re }]) => {
    let value = answered(latest, ids, re);
    for (let i = 0; value == null && i < older.length; i += 1) {
      value = answered(older[i], ids, re);
    }
    out[key] = value;
  });
  return out;
}

/* ── Root causes ─────────────────────────────────────────────────────── */

/**
 * Root causes, read from whichever shape the report supplies.
 *
 * `dgReport/result` nests them under root_causes.primaryRootCause /
 * .defaultRootCause; `dgReport/female` does not always use that shape, and
 * some leads legitimately come back with none at all. Reading only the one
 * path left the Root Cause step with nothing to explain even when the report
 * did carry them, so every known shape is accepted and an empty list stays a
 * real, handled state rather than an error.
 */
/**
 * "hormonalImbalance" -> "Hormonal Imbalance". The report sends the machine key
 * for most root causes and only sometimes a display name, so the key was
 * reaching the coach raw.
 */
function humaniseKey(key) {
  return String(key || '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());
}

function readRootCauses(rootCauseData) {
  const rc = _.get(rootCauseData, 'root_causes', null);
  const candidates = [
    _.get(rc, 'primaryRootCause'),
    _.get(rc, 'defaultRootCause'),
    Array.isArray(rc) ? rc : null,
    _.get(rootCauseData, 'rootCauses'),
    _.get(rootCauseData, 'primaryRootCause'),
    _.get(rootCauseData, 'defaultRootCause'),
  ].filter(Array.isArray);

  return candidates.flat()
    .filter(Boolean)
    .map(rc => {
      const key = _.get(rc, 'key') || _.get(rc, 'category') || '';
      const name = _.get(rc, 'name') || _.get(rc, 'label') || '';
      return { category: key, label: name || humaniseKey(key) };
    })
    .filter(rc => rc.label);
}

/* ── Kit ─────────────────────────────────────────────────────────────── */

function readKit(rootCauseData) {
  const products = _.get(rootCauseData, 'product_Ids', []) || [];
  return {
    kit_id: null,
    currency: 'INR',
    changed_by_coach: false,
    change_summary: null,
    items: products.map(p => ({
      product_id: _.get(p, 'product_id'),
      // cartDisplayName is the name the Result page prints; `name` is the
      // internal one. Quoting the same string keeps the two screens identical.
      name: _.get(p, 'cartDisplayName') || _.get(p, 'name', ''),
      quantity: Number(_.get(p, 'itemCount', 1)) || 1,
      // null, not 0, when the Result omits a price — 0 is a real price the kit
      // block must show as-is rather than quietly swapping in a catalogue one.
      price: _.get(p, 'price') == null ? null : Number(_.get(p, 'price')) || 0,
      tag: _.get(p, ['tag', 'title'], '') || '',
      oneLiner: _.get(p, 'oneLiner', '') || '',
      description: _.get(p, 'description', '') || '',
      image: _.get(p, ['image_url', 'cartImgUrl'])
        || _.get(p, ['image_url', 'productUrl'], '') || '',
    })).filter(i => i.product_id || i.name),
  };
}

/* ── Expectation ─────────────────────────────────────────────────────── */

function readExpectation(expectationData) {
  /**
   * For a FEMALE lead the drawer quotes `kits_before_care_plan`, not
   * `monthsDetails.months` (ExpectationDrawerContent.jsx:483) — the two differ
   * once a care plan is attached, and the coach was reading the wrong one.
   */
  const femaleKits = _.get(expectationData, ['expectationSettingsCrm', 'kits_before_care_plan'], null);
  const months = femaleKits != null && femaleKits !== ''
    ? femaleKits
    : _.get(expectationData, ['monthsDetails', 'months'], null);
  const rows = _.get(expectationData, ['monthsDetails', 'monthExpectations'], []) || [];
  // null, not 0, when the plan is genuinely unknown: 0 is a real value to
  // crmMark and rendered as "[ 0 ]" where the coach expected an em dash.
  const parsed = months != null && months !== '' ? Number(months) : NaN;
  const total = Number.isFinite(parsed) && parsed > 0 ? parsed : (rows.length || null);
  return {
    total_kits: total,
    duration_months: total,
    running_kit: Number(_.get(expectationData, ['monthsDetails', 'nonVoidKitCount'], 0)) || 0,
    maintenance_from_month: null,
    /**
     * The backend's per-month rows stand in for the prototype's kitwise copy.
     *
     * Rows are `{ month, goal, description }` — the same fields
     * ExpectationDrawerContent.jsx:431-435 reads. They were being read as
     * `expectation` / `en` / `hi`, none of which exist, so every kit rendered
     * its [ kitwise[n].hi ] gap chip.
     *
     * `getMonthsDiagnosis` returns ONE language per call (?lang=), so the text
     * that comes back is already in the requested language and fills both
     * slots; switching the toggle refetches. The list is padded to the full kit
     * count so a kit the backend has no copy for is still listed and still
     * reported by the MISSING CRM line, rather than silently vanishing.
     */
    kitwise: Array.from({ length: Math.max(total || 0, rows.length) }, (_unused, i) => {
      const row = rows[i];
      const text = _.get(row, 'description') || _.get(row, 'goal') || null;
      return { kit_label: `Kit ${_.get(row, 'month') || i + 1}`, en: text, hi: text };
    }),
    phases: _.get(expectationData, ['expectationSettingsCrm', 'phase_timeline'], []) || [],
  };
}

/* ── Public builder ──────────────────────────────────────────────────── */

/**
 * The engagement tag the CRM books a lead call under, mapped to the slot type
 * the prototype's conditions are written against.
 *
 * The raw tag was being stored here verbatim, so `consultation_type` held
 * "chat_slot_booking" while every trigger tests for "CHAT_SLOT". Nothing ever
 * matched: the chat-slot only-if could not arm, and Introduction fell through
 * to its "slot type not set" warning on every single call.
 *
 * Tags are the ones LeadDetails.jsx:83 routes pre-sales female on, plus the
 * IVR tags at :23.
 */
const SLOT_TYPES = {
  SLOT_BOOKING: 'LEAD_SLOT',
  lead_cold_slot: 'LEAD_SLOT',
  chat_slot_booking: 'CHAT_SLOT',
  IVR_BOOKING: 'IVR_SLOT',
  IVR_CALLBACK: 'IVR_SLOT',
};

export function slotType(engagementTag) {
  if (!engagementTag) return 'LEAD_SLOT';
  if (SLOT_TYPES[engagementTag]) return SLOT_TYPES[engagementTag];
  // Already an enum (or an unmapped tag): normalise rather than guess.
  const t = String(engagementTag).toUpperCase().replace(/[^A-Z]+/g, '_');
  if (t.includes('CHAT')) return 'CHAT_SLOT';
  if (t.includes('IVR')) return 'IVR_SLOT';
  return 'LEAD_SLOT';
}

/**
 * Whole months since the hair test was taken. The Introduction step warns the
 * coach when this is over 6, so an unknown date must not read as "fresh".
 */
function ageMonths(completedAt) {
  if (!completedAt) return null;
  const then = new Date(completedAt);
  if (isNaN(then.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - then.getTime()) / (1000 * 60 * 60 * 24 * 30.44)));
}

export function buildFemaleCrm({
  customerData,
  caseFormResponses,
  rootCauseData,
  expectationData,
  scalpImages,
  agentName,
  engagementTag,
  kitSource,
  addresses,
} = {}) {
  const user = _.get(customerData, 'user', {}) || {};
  const orders = _.get(customerData, 'orders', []) || [];
  const latestOrder = orders[orders.length - 1] || null;
  /**
   * Where the delivery address comes from.
   *
   * A pre-sales lead has no orders, so the order's shipping address is always
   * empty — the address the agent will actually use at Place Order is the one
   * on the customer profile (`address/{userId}`, read through useAddressStore
   * the same way ProductsOffcanvas.jsx:358-360 does). The default address wins,
   * falling back to the first, then to the order's own shipping address for a
   * lead that somehow already has one.
   */
  const saved = (addresses || []).find(a => a && a.is_default) || (addresses || [])[0] || null;
  const orderShipping = _.get(latestOrder, ['order_meta', 'shipping_address'], {}) || {};
  const shipping = saved
    ? {
      address1: saved.address1, address2: saved.address2, city: saved.city,
      province: saved.province, zip: saved.zip,
    }
    : orderShipping;
  const answers = readAnswers(customerData, caseFormResponses);
  const applicable = _.get(rootCauseData, 'applicable_discount', {}) || {};
  const oneMonth = (_.get(applicable, 'discounts', []) || [])
    .find(d => Number(_.get(d, 'month')) === 1);
  const completedAt = _.get(latestResponses(customerData, caseFormResponses), [0, 'created_at'], null);

  return {
    // Which hair-test report the kit came from — the female and generic
    // reports carry different product sets, so this is worth being able to see.
    meta: { source: kitSource || 'CRM', test_version: 1 },

    customer: {
      customer_id: _.get(customerData, 'id', null),
      full_name: [user.first_name, user.last_name].filter(x => x && x !== 'null').join(' ') || null,
      phone: user.phone_number || null,
      email: user.email || null,
      age: _.get(answers, 'age', null) || user.age || null,
      gender: 'FEMALE',
      city: user.city || shipping.city || null,
      pincode: user.pincode || shipping.zip || null,
      preferred_language: user.preferred_language || null,
    },

    consultation: {
      consultation_type: slotType(engagementTag),
      agent_name: agentName || null,
      is_first_consultation: orders.length === 0,
      // No chat-transcript feed on the lead yet, so the chat-continuity branch
      // stays off rather than promising a summary the coach cannot see.
      chat_has_open_query: false,
      chat_query_summary: null,
    },

    hair_test: {
      status: Object.values(answers).some(Boolean) ? 'COMPLETE' : 'MISSING',
      completed_at: completedAt,
      age_months: ageMonths(completedAt),
      answers,
    },

    root_causes: readRootCauses(rootCauseData),

    /**
     * Field names are the ones the scalp-image list actually returns, as read
     * by actionables_v2/scalp_image/ScalpImageNew.jsx:29-33 — `imagePath`,
     * `createdAt`, `taggedStage`. Guessing at `url` / `image_type` left every
     * photo row showing its red gap chip even though the images were on file.
     */
    media: {
      scalp_photos: (scalpImages || []).map(img => {
        const stage = _.get(img, 'taggedStage') || null;
        const at = _.get(img, 'createdAt') || _.get(img, 'created_at') || null;
        return {
          photo_id: _.get(img, 'id', null),
          url: _.get(img, 'imagePath') || _.get(img, 'imageUrl') || null,
          // The stage the coach tagged is the closest thing to the prototype's
          // CROWN / FRONT view; an untagged photo says so rather than inventing one.
          view_type: stage || 'Untagged',
          captured_at: at ? moment(at).tz('Asia/Kolkata').format("DD MMM 'YY") : null,
          source: 'HAIR_TEST',
          // Tagging a stage is the review — that is what the tag records.
          reviewed_by_coach: !!stage,
        };
      }),
    },

    // Derm/coach notes are not exposed on the lead record; the section simply
    // renders nothing for an empty list.
    coach_notes: [],

    kit: readKit(rootCauseData),
    expectation: readExpectation(expectationData),

    commercial: {
      // Only honour a discount the report actually marked valid.
      discount_pct: _.get(applicable, 'is_valid') === true && _.get(oneMonth, 'type') === 'percentage'
        ? Number(_.get(oneMonth, 'value', 0)) || null
        : null,
      coupon_codes: (_.get(applicable, 'discounts', []) || [])
        .map(d => _.get(d, 'coupon_code'))
        .filter(Boolean),
      coupon_expiry_text: _.get(applicable, 'expiry_text', null),
      supplement_course_months: null,
      free_services: null,
      women_helped_lakhs: 20,
      delivery_sla_text: '5 to 7 working days',
    },

    order: {
      address_full: [shipping.address1, shipping.address2, shipping.city]
        .filter(Boolean).join(', ') || null,
      pincode: shipping.zip || user.pincode || null,
      city: shipping.city || null,
      state: shipping.province || null,
      alt_contact_number: null,
      final_bill: null,
      payment_modes: null,
      is_first_order: orders.length === 0,
      order_status: latestOrder ? (_.get(latestOrder, 'status', null) || 'PLACED') : 'NONE',
      previous_orders: orders.length,
      // null renders as "CRM re-check pending" — the honest state, since
      // serviceability is only known once the pincode is checked server-side.
      delivery_serviceable: null,
      order_confirmation_code_required: false,
    },

    app: {
      app_installed: _.get(customerData, 'app_user', null),
      // Configured per environment in config/env.js, not hard-coded here.
      app_link: TRAYA_APP_LINK,
    },

    // GAP 1 in the prototype: the three stage taxonomies mean money-back
    // eligibility cannot be derived from hair_volume. Until the backend
    // supplies it, null keeps the guarantee unmentioned.
    eligibility: { moneyback: null },
  };
}

export default buildFemaleCrm;
